import { useMemo } from 'react';
import { cn } from '@rocco/utils/cn';
import type { MeUser } from '~/server/types';
import { useConversation } from '../../context';
import { getChatAvatarParticipants } from '../../utils';
import { useConversationActiveStatus } from '../../hooks/use-conversation-active-status';
import { GroupChatAvatar } from '../avatar/GroupChatAvatar';
import { ThumbnailAvatar } from '../avatar/ThumbnailAvatar';

/**
 * The avatar component for a chat box. This is determined by the inbox context,
 * conversation type, participants, and thumbnail settings.
 * @see {@link ChatBox}
 */
export function ChatBoxAvatar({
    user,
    isOpen,
}: {
    /**
     * The current logged-in user.
     */
    user: MeUser;
    /**
     * Whether the chat box is open.
     */
    isOpen: boolean;
}) {
    const { professionalID, conversation } = useConversation();
    const isDirectConversation =
        conversation?.__typename === 'DirectConversation';
    const isGroupChat = isDirectConversation && conversation.IsGroupChat;
    const participants = useMemo(() => {
        return getChatAvatarParticipants(conversation, user.ID, professionalID);
    }, [conversation, user.ID, professionalID]);
    const activeStatus = useConversationActiveStatus(conversation);

    return isDirectConversation && conversation.Thumbnail?.Link ? (
        <ThumbnailAvatar
            className={cn(isOpen ? 'size-[40px]' : 'size-9')}
            src={conversation.Thumbnail.Link}
            activeStatus={activeStatus ?? undefined}
        />
    ) : (
        <GroupChatAvatar
            className={cn(isOpen ? 'size-[40px]' : 'size-9')}
            avatarClassName={cn(isOpen ? 'size-[40px]' : 'size-9')}
            fallbackClassName={cn(isOpen ? 'text-[18px]!' : 'text-4.5!')}
            participants={participants}
            activeStatus={activeStatus ?? undefined}
            maxAvatars={isGroupChat ? 7 : 1}
        />
    );
}
